import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, ID, registerEnumType, Int } from 'type-graphql'

import { Domain, ScalarObject } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'

export enum ScopeType {
  GEOGRAPHIC = 'GEOGRAPHIC', // 지리적 차원 (지역, 시도, 구군)
  ORGANIZATIONAL = 'ORGANIZATIONAL', // 조직 차원 (회사, 부서, 팀)
  BUSINESS = 'BUSINESS', // 비즈니스 차원 (규모, 유형, 등급)
  TECHNICAL = 'TECHNICAL', // 기술적 차원 (공종, 기술, 방법)
  TEMPORAL = 'TEMPORAL', // 시간적 차원 (분기, 년도, 기간)
  CUSTOM = 'CUSTOM' // 사용자 정의
}

registerEnumType(ScopeType, {
  name: 'ScopeType',
  description: 'Types of organizational scope dimensions'
})

@Entity()
@Index('ix_kpi_scope_domain', (kpiScope: KpiScope) => [kpiScope.domain])
@Index('ix_kpi_scope_level', (kpiScope: KpiScope) => [kpiScope.domain, kpiScope.level])
@ObjectType({
  description: 'Defines the meaning and metadata for each scope level (scope01~05) in KpiOrgScope'
})
export class KpiScope {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID)
  readonly id: string

  // 스코프 레벨 (1-5)
  @Column({ type: 'int' })
  @Field(type => Int, { description: 'Scope level (1-5) corresponding to scope01-scope05 in KpiOrgScope' })
  level: number

  // 스코프 차원 이름
  @Column()
  @Field({ description: 'Display name for this scope dimension (e.g., "지역", "회사", "프로젝트규모")' })
  name: string

  // 스코프 차원 설명
  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Detailed description of what this scope dimension represents' })
  description?: string

  // 스코프 타입 (지리적, 조직적, 비즈니스 등)
  @Column()
  @Field(type => ScopeType, { description: 'Type category of this scope dimension' })
  scopeType: ScopeType

  // 계층 정보
  @Column({ nullable: true })
  @Field(type => Int, { nullable: true, description: 'Parent scope level if this forms a hierarchy' })
  parentLevel?: number

  @Column({ default: 1 })
  @Field(type => Int, { description: 'Order/priority for display and processing' })
  displayOrder: number

  // 유효한 값들 (선택사항)
  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description: 'List of valid values for this scope (e.g., ["서울", "부산", "대구"] for regions)'
  })
  validValues?: string[]

  // 정규표현식 검증 (선택사항)
  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: 'Regex pattern for validating values in this scope dimension'
  })
  validationPattern?: string

  // 활성화 여부
  @Column({ default: true })
  @Field({ description: 'Whether this scope definition is active and should be used' })
  active: boolean

  // 통계 계산에 사용 여부
  @Column({ default: true })
  @Field({ description: 'Whether this scope should be included in statistical calculations' })
  includeInStatistics: boolean

  // 대시보드에 표시 여부
  @Column({ default: false })
  @Field({ description: 'Whether this scope should be displayed in dashboard visualizations' })
  showInDashboard: boolean

  // 메타데이터
  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Additional metadata for this scope dimension (colors, icons, etc.)'
  })
  metadata?: {
    color?: string
    icon?: string
    unit?: string
    [key: string]: any
  }

  // === 표준 필드들 ===
  @ManyToOne(type => Domain)
  @Field({ nullable: true, description: 'Domain this scope definition belongs to' })
  domain?: Domain

  @RelationId((kpiScope: KpiScope) => kpiScope.domain)
  domainId?: string

  @CreateDateColumn()
  @Field({ nullable: true })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  creator?: User

  @RelationId((kpiScope: KpiScope) => kpiScope.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  updater?: User

  @RelationId((kpiScope: KpiScope) => kpiScope.updater)
  updaterId?: string
}
